import { create } from "zustand";
import {
  assistantCost,
  assistantProvider,
  chatModelList,
  isAssistantProvider,
  spendStatus,
  type AssistantMessage,
  type AssistantModelPick,
  type AssistantProviderId,
  type AssistantUsage,
  isAgentEffort,
  resolveEffort,
  type AgentEffort,
  type AgentReasoning,
} from "@ide/shared/assistant";
import { desktop } from "@/desktop/bridge";
import { useAiUsage } from "@/lib/ai-usage";
import { activeEditor } from "@/workspace/active-editor";
import type { WorkspaceContextValue } from "@/workspace/context";
import { useLocalCopy } from "@/workspace/local-copy";
import { useWorkspace } from "@/workspace/store";
import { agentDb } from "./agent-db";
import { beginCheckpoint, currentCheckpoint, rememberOriginal } from "./checkpoints";
import { recordAfter, recordBefore } from "./ai-changes";
import { readFile } from "@/workspace/file-ops";
import { runLoop, type LoopStop } from "./loop";
import { formatSpend, knownPrice, spendOf, useAgentSpend } from "./spend";
import { checkInput, isSecretPath, toolByName, toolDefs, type ToolKind } from "./tools";

/**
 * Estado do agente (por projeto aberto) e o que liga o laço às ferramentas, às permissões e ao
 * app. A conversa fica na memória desta janela: fechar o projeto encerra a conversa.
 */

/** Como o agente pede permissão (como os modos do Claude Code). */
export type AgentMode = "ask" | "edits" | "auto" | "plan";

export const MODES: Record<AgentMode, { label: string; hint: string }> = {
  ask: {
    label: "Pedir permissão",
    hint: "Lê sozinho; pergunta antes de mudar arquivos, rodar comandos ou usar o Git",
  },
  edits: {
    label: "Aceitar edições",
    hint: "Cria e edita arquivos sem perguntar; pergunta antes de apagar, rodar comandos e usar o Git",
  },
  auto: { label: "Tudo liberado", hint: "Faz tudo sem perguntar. Use só em projetos que você confia" },
  plan: { label: "Só planejar", hint: "Investiga e entrega um plano, sem mudar nada" },
};

export type StepStatus = "preparing" | "waiting" | "running" | "done" | "error" | "denied";

export type AgentItem =
  | { kind: "user"; id: string; text: string; selection?: string }
  | { kind: "text"; id: string; text: string }
  | { kind: "thinking"; id: string; text: string }
  | {
      kind: "tool";
      id: string;
      name: string;
      tool: ToolKind;
      status: StepStatus;
      title: string;
      patch?: string;
      path?: string;
      command?: string;
      detail?: string;
      result?: string;
      note?: string;
      output?: string;
    }
  | {
      kind: "notice";
      id: string;
      tone: "info" | "error";
      text: string;
      auth?: boolean;
      /** Aviso do limite de gasto: o painel oferece "Ajustar limite". */
      budget?: boolean;
      /** O modelo escolhido pela pessoa falhou: o painel oferece a alternativa ou o automático. */
      modelFix?: { model: string; alternative?: string };
    }
  /**
   * Fim de uma resposta: os arquivos que o agente mudou (com "Desfazer") e a avaliação 👍/👎.
   */
  | {
      kind: "done";
      id: string;
      provider: AssistantProviderId;
      model: string;
      effort: AgentReasoning;
      /** Ponto de restauração deste pedido (se mudou algum arquivo). */
      checkpointId?: string;
      files?: string[];
      rating?: "up" | "down";
    };

export type ToolItem = Extract<AgentItem, { kind: "tool" }>;

export interface Decision {
  allow: boolean;
  /** Vale para este tipo de ação até o fim da conversa. */
  always?: boolean;
  /** O que a pessoa quer em vez disso (vai para o modelo). */
  feedback?: string;
}

interface Usage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  cost: number | null;
}

const EMPTY_USAGE: Usage = {
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  cost: 0,
};

interface Prefs {
  /** Conexão de IA escolhida pelo nome (Configurações › Conexões de IA). */
  connectionId: string | null;
  provider: AssistantProviderId | null;
  models: Partial<Record<AssistantProviderId, string>>;
  /**
   * Modelos escolhidos pelo Kompil (automático). Só esses podem ser trocados sozinhos quando falham;
   * o que a pessoa escolheu fica, e o agente avisa com opções.
   */
  auto: Partial<Record<AssistantProviderId, boolean>>;
  /** Esforço escolhido (Automático decide por pedido). */
  effort: AgentEffort;
  mode: AgentMode;
}

/** Versão das preferências gravadas (sobe quando o formato antigo não vale mais). */
const PREFS_VERSION = 3;

function readPrefs(): Prefs {
  const prefs: Prefs = {
    connectionId: null,
    provider: null,
    models: {},
    auto: {},
    effort: "auto",
    mode: "ask",
  };
  try {
    const raw = JSON.parse(localStorage.getItem("kompil.agent") ?? "{}") as Partial<Prefs> & { v?: number };
    if (isAssistantProvider(raw.provider)) prefs.provider = raw.provider;
    if (typeof raw.connectionId === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(raw.connectionId))
      prefs.connectionId = raw.connectionId;
    // Versões anteriores gravavam o primeiro modelo da lista em ordem alfabética (ex.: aion-labs)
    // sem a pessoa escolher, e depois o tratavam como escolha dela. Só vale o que foi gravado a
    // partir desta versão (v3), com a marca de quem escolheu; o resto o Kompil escolhe de novo.
    if (raw.v === PREFS_VERSION) {
      if (raw.auto && typeof raw.auto === "object") prefs.auto = raw.auto;
      if (raw.models && typeof raw.models === "object") prefs.models = raw.models;
    }
    if (isAgentEffort(raw.effort)) prefs.effort = raw.effort;
    // "Tudo liberado" não é lembrado: cada sessão começa pedindo permissão.
    if (raw.mode === "edits" || raw.mode === "plan") prefs.mode = raw.mode;
  } catch {
    // sem armazenamento: o padrão
  }
  return prefs;
}

interface AgentState extends Prefs {
  projectId: string | null;
  messages: AssistantMessage[];
  items: AgentItem[];
  running: boolean;
  /** Tipos de ação liberados pela pessoa nesta conversa ("sempre"). */
  granted: ToolKind[];
  usage: Usage;
  /** Janela grande (o agente ocupa a área do editor). */
  wide: boolean;
  setPrefs(p: Partial<Prefs>): void;
  setWide(wide: boolean): void;
  /** Troca de projeto: a conversa deste projeto volta de onde parou (memória do agente). */
  reset(projectId: string | null): void;
  /** "Nova conversa": começa do zero e esquece a anterior deste projeto. */
  newConversation(projectId: string | null): void;
}

export const useAgentStore = create<AgentState>((set, get) => ({
  ...readPrefs(),
  projectId: null,
  messages: [],
  items: [],
  running: false,
  granted: [],
  usage: EMPTY_USAGE,
  wide: false,
  setPrefs: (p) => {
    set(p);
    const { connectionId, provider, models, auto, effort, mode } = get();
    try {
      localStorage.setItem(
        "kompil.agent",
        JSON.stringify({ v: PREFS_VERSION, connectionId, provider, models, auto, effort, mode }),
      );
    } catch {
      // sem armazenamento: vale só agora
    }
  },
  setWide: (wide) => set({ wide }),
  reset: (projectId) => {
    abortCurrent();
    set({ projectId, messages: [], items: [], running: false, granted: [], usage: EMPTY_USAGE });
    if (projectId) void restoreConversation(projectId);
  },
  newConversation: (projectId) => {
    abortCurrent();
    // A conversa atual vai para o histórico (dá para reabrir depois).
    void archiveConversation();
    set({ projectId, messages: [], items: [], running: false, granted: [], usage: EMPTY_USAGE });
    if (projectId) void forgetConversation(projectId);
  },
}));

// ───────────── Memória: a conversa de cada projeto fica guardada neste computador ─────────────

interface SavedConversation {
  projectId: string;
  at: number;
  messages: AssistantMessage[];
  items: AgentItem[];
}

async function restoreConversation(projectId: string) {
  const saved = await agentDb<SavedConversation | undefined>("conversations", "readonly", (s) =>
    s.get(projectId),
  ).catch(() => undefined);
  const s = useAgentStore.getState();
  // Só se ainda é o mesmo projeto e nada começou enquanto lia.
  if (!saved || s.projectId !== projectId || s.items.length || s.running) return;
  useAgentStore.setState({ messages: saved.messages, items: saved.items });
}

function saveConversation() {
  const { projectId, messages, items } = useAgentStore.getState();
  if (!projectId || !items.length) return;
  const data: SavedConversation = { projectId, at: Date.now(), messages, items };
  void agentDb("conversations", "readwrite", (s) => s.put(data)).catch(() => undefined);
}

function forgetConversation(projectId: string) {
  return agentDb("conversations", "readwrite", (s) => s.delete(projectId)).catch(() => undefined);
}

// ───────────── Histórico: conversas anteriores do agente (neste computador) ─────────────

export interface ArchivedConversation {
  id: string;
  projectId: string;
  at: number;
  title: string;
  messages: AssistantMessage[];
  items: AgentItem[];
}

const HISTORY_KEEP = 40;

/** Guarda a conversa atual no histórico (se tiver alguma coisa). */
async function archiveConversation() {
  const { projectId, messages, items } = useAgentStore.getState();
  if (!projectId || !items.length) return;
  const first = items.find((i) => i.kind === "user");
  const title = (first && first.kind === "user" ? first.text : "Conversa")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  const data: ArchivedConversation = {
    id: crypto.randomUUID(),
    projectId,
    at: Date.now(),
    title,
    messages,
    items,
  };
  await agentDb("history", "readwrite", (s) => s.put(data)).catch(() => undefined);
  // Só as mais recentes ficam.
  const all = await listAgentHistory(projectId);
  for (const old of all.slice(HISTORY_KEEP))
    void agentDb("history", "readwrite", (s) => s.delete(old.id)).catch(() => undefined);
}

/** Conversas anteriores do projeto, mais recentes primeiro. */
export async function listAgentHistory(projectId: string): Promise<ArchivedConversation[]> {
  const list = await agentDb<ArchivedConversation[]>("history", "readonly", (s) =>
    s.index("projectId").getAll(projectId),
  ).catch(() => [] as ArchivedConversation[]);
  return list.sort((a, b) => b.at - a.at);
}

/** Reabre uma conversa do histórico (a atual vai para o histórico antes). */
export async function openAgentHistory(entry: ArchivedConversation) {
  const s = useAgentStore.getState();
  if (s.running) return;
  if (s.items.length) await archiveConversation();
  await agentDb("history", "readwrite", (st) => st.delete(entry.id)).catch(() => undefined);
  useAgentStore.setState({
    projectId: entry.projectId,
    messages: entry.messages,
    items: entry.items,
    granted: [],
    usage: EMPTY_USAGE,
  });
  saveConversation();
}

// ───────────── Avaliação das respostas ─────────────

/** 👍/👎 numa resposta: fica marcado na conversa e guardado (por modelo) neste computador. */
export function rateAnswer(itemId: string, rating: "up" | "down" | undefined) {
  const item = useAgentStore.getState().items.find((i) => i.id === itemId);
  if (!item || item.kind !== "done") return;
  patchItem(itemId, (i) =>
    i.kind === "done" ? { ...i, ...(rating ? { rating } : { rating: undefined }) } : i,
  );
  try {
    const key = "kompil.agent.ratings";
    const list = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown[];
    const next = (Array.isArray(list) ? list : []).filter(
      (r) => (r as { id?: string } | null)?.id !== itemId,
    );
    if (rating)
      next.push({
        id: itemId,
        at: Date.now(),
        provider: item.provider,
        model: item.model,
        effort: item.effort,
        rating,
      });
    localStorage.setItem(key, JSON.stringify(next.slice(-300)));
  } catch {
    // sem armazenamento: a avaliação vale só nesta conversa
  }
  saveConversation();
}

const uid = () => crypto.randomUUID();

function patchItem(id: string, fn: (item: AgentItem) => AgentItem) {
  useAgentStore.setState((s) => ({ items: s.items.map((i) => (i.id === id ? fn(i) : i)) }));
}
function patchTool(id: string, patch: Partial<ToolItem>) {
  patchItem(id, (i) => (i.kind === "tool" ? { ...i, ...patch } : i));
}
const pushItem = (item: AgentItem) => useAgentStore.setState((s) => ({ items: [...s.items, item] }));

// ───────────── Permissões ─────────────

const waiting = new Map<string, (d: Decision) => void>();

/** Resposta da pessoa a um pedido de permissão (botões do passo). */
export function decide(toolId: string, decision: Decision) {
  const resolve = waiting.get(toolId);
  if (!resolve) return;
  waiting.delete(toolId);
  if (decision.allow && decision.always) {
    const kind = useAgentStore
      .getState()
      .items.find((i): i is ToolItem => i.kind === "tool" && i.id === toolId)?.tool;
    if (kind) useAgentStore.setState((s) => ({ granted: [...new Set([...s.granted, kind])] }));
  }
  resolve(decision);
}

/** Libera sem perguntar? `deny` no modo planejar (nada muda). */
export function autoDecision(
  kind: ToolKind,
  mode: AgentMode,
  granted: readonly ToolKind[],
): "allow" | "ask" | "deny" {
  if (kind === "read") return "allow";
  if (mode === "plan") return "deny";
  if (mode === "auto" || granted.includes(kind)) return "allow";
  if (mode === "edits" && kind === "edit") return "allow";
  return "ask";
}

// ───────────── Instruções e contexto ─────────────

const OS = { win32: "Windows (PowerShell)", darwin: "macOS (zsh)", linux: "Linux (bash)" } as const;

export function systemPrompt(mode: AgentMode): string {
  return [
    "Você é o agente de código do Kompil, uma IDE colaborativa. Você trabalha no projeto aberto pela pessoa usando as ferramentas disponíveis, como um engenheiro sênior cuidadoso.",
    "",
    "Como trabalhar:",
    "- Responda sempre em português do Brasil, direto e sem enrolação. Antes de agir, diga em uma frase o que vai fazer.",
    "- Entenda antes de mudar: use list_files, search e read_file. Leia o arquivo antes de editá-lo.",
    "- Para mudanças pontuais use edit_file (trecho exato, único); para arquivos novos ou reescritas, write_file. Mantenha o estilo, a indentação e as convenções do projeto.",
    "- Faça só o que foi pedido. Pedidos grandes (ex.: criar um projeto inteiro) você faz por partes, arquivo por arquivo, com uma estrutura de pastas organizada, README e instruções de execução.",
    "- Toda mudança entra como edição da pessoa: o time vê na hora e fica no histórico.",
    "- Ações que mudam algo (arquivos, comandos, Git) passam pela permissão da pessoa. Se ela negar, não insista: leia o motivo, ajuste o plano ou pergunte.",
    "",
    "Comandos:",
    `- O computador da pessoa usa ${OS[desktop?.platform ?? "linux"]}. Os comandos rodam na pasta do projeto, sem entrada interativa (use --yes, -y, --no-input).`,
    "- run_command é para o que termina (instalar dependências, build, testes, lint). Servidores e processos que ficam rodando (npm run dev, streamlit run, flask run) vão em start_in_terminal.",
    "- Python: use um ambiente virtual no projeto (python -m venv .venv e o pip de dentro dele), nunca instale no sistema. Node: use o gerenciador que o projeto já usa (lockfile).",
    "- Depois de mudar código, rode os testes ou o build quando existirem e corrija o que quebrar.",
    "",
    "Segurança e privacidade (LGPD):",
    "- Nunca leia, mostre, peça ou grave segredos reais (arquivos .env, chaves, senhas, tokens). Use variáveis de ambiente e um .env.example com valores de exemplo.",
    "- Não envie dados pessoais para serviços externos sem a pessoa pedir. Não desative verificações de segurança.",
    "- Comandos destrutivos (apagar pastas, reescrever histórico do Git, force push) só se a pessoa pedir explicitamente.",
    "",
    "Git e hospedagem:",
    "- Confira com git_status. Para publicar: git_create_repository (novo, privado por padrão) ou git_link_repository (existente), e git_commit_push com mensagem clara no idioma do projeto.",
    "- Hospedar: use a CLI do serviço com run_command (ex.: npx vercel --prod --yes, npx netlify-cli deploy --prod, npx firebase-tools deploy). Se pedir login, explique à pessoa como entrar pelo terminal do Kompil e pare.",
    "",
    "Ao terminar, faça um resumo curto: o que mudou (arquivos, comandos), como testar e o que falta a pessoa fazer.",
    ...(mode === "plan"
      ? [
          "",
          "MODO SÓ PLANEJAR: não altere nada (as ferramentas de mudança estão bloqueadas). Investigue e entregue um plano numerado, com os arquivos que mudariam, para a pessoa aprovar.",
        ]
      : []),
  ].join("\n");
}

export function projectContext(ws: WorkspaceContextValue): string {
  const tree = ws.session.tree();
  const files = [...tree.paths]
    .filter(([id]) => tree.nodes.get(id)?.type === "file")
    .map(([, path]) => path)
    .sort();
  const active = useWorkspace.getState().active;
  const activePath = active ? tree.paths.get(active) : undefined;
  const local = useLocalCopy.getState();
  const localOn = local.projectId === ws.detail.project.id && local.state === "on";
  return [
    `Projeto: ${ws.detail.project.name}${ws.detail.project.description ? ` — ${ws.detail.project.description}` : ""}`,
    `Papel da pessoa: ${ws.detail.role}${ws.session.canWrite ? "" : " (só leitura: não pode alterar arquivos)"}`,
    `Salvar na máquina: ${localOn ? "ligado (comandos rodam na pasta do projeto)" : "desligado (comandos indisponíveis até a pessoa ligar)"}`,
    activePath ? `Arquivo aberto no editor: ${activePath}` : "Nenhum arquivo aberto no editor.",
    `Arquivos (${files.length}${files.length > 500 ? ", primeiros 500" : ""}):`,
    files.length
      ? files
          .slice(0, 500)
          .map((p) => (isSecretPath(p) ? `${p} (segredo)` : p))
          .join("\n")
      : "(projeto vazio)",
  ].join("\n");
}

// ───────────── Execução ─────────────

let controller: AbortController | null = null;
let currentRequest: string | null = null;

function abortCurrent() {
  controller?.abort();
  if (currentRequest) desktop?.assistant.cancel(currentRequest);
  for (const [id, resolve] of waiting) {
    waiting.delete(id);
    resolve({ allow: false });
  }
}

/** Para o agente (o turno, o comando em execução e os pedidos de permissão abertos). */
export function stopAgent() {
  abortCurrent();
}

export interface SendOptions {
  ws: WorkspaceContextValue;
  text: string;
  withSelection: boolean;
}

/**
 * Grava o modelo de um provedor. `auto`: o Kompil escolheu (pode trocar sozinho se falhar); senão,
 * foi a pessoa (o agente nunca troca).
 */
export function setModel(provider: AssistantProviderId, model: string, auto: boolean) {
  const s = useAgentStore.getState();
  s.setPrefs({ models: { ...s.models, [provider]: model }, auto: { ...s.auto, [provider]: auto } });
}

/**
 * O app foi atualizado mas o processo principal ainda é o antigo (só muda ao reiniciar o Kompil):
 * faltam funções novas, como escolher o modelo testando na conta.
 */
export const desktopOutdated = () =>
  Boolean(desktop?.assistant) && typeof desktop?.assistant.pickModel !== "function";

/**
 * Escolhe o modelo sozinho: pela API (testando na conta) quando o app tem essa função; senão,
 * pelo recomendado para programar da lista do provedor (nunca o primeiro em ordem alfabética).
 */
export async function autoPickModel(
  provider: AssistantProviderId,
  exclude: string[] = [],
): Promise<AssistantModelPick> {
  const api = desktop!.assistant;
  if (typeof api.pickModel === "function") return api.pickModel(provider, exclude);
  const list = await api.models(provider);
  const ids = list.models.filter((m) => !exclude.includes(m));
  const ranked = chatModelList(
    provider,
    ids.map((id) => ({ id })),
  );
  const model = ranked.recommended[0] ?? ranked.models[0];
  return model
    ? { ok: true, model, reason: "Recomendado para programar (reinicie o Kompil para o teste na sua conta)." }
    : { ok: false, error: list.error ?? "Este provedor não informou modelos que aceitem ferramentas." };
}

/** Nome do provedor para os avisos ("OpenAI", "Anthropic"). */
const providerLabel = (id: AssistantProviderId) => assistantProvider(id).name.replace(/ \(.+\)$/, "");

const limitText = (provider: AssistantProviderId) => {
  const { limit } = spendOf(provider);
  return `Você chegou ao limite de ${formatSpend(limit ?? 0)} deste mês em ${providerLabel(provider)}. Aumente o limite para continuar ou espere o próximo mês.`;
};

export async function sendToAgent({ ws, text, withSelection }: SendOptions): Promise<LoopStop | null> {
  const api = desktop?.assistant;
  const state = useAgentStore.getState();
  if (!api || state.running) return null;
  if (state.projectId !== ws.detail.project.id) state.reset(ws.detail.project.id);
  // A conexão escolhida pelo nome: provedor, chave e modelo confirmados nas configurações.
  const connections = typeof api.connections === "function" ? await api.connections().catch(() => []) : [];
  const conn = connections.find((c) => c.id === state.connectionId) ?? connections[0] ?? null;
  if (!conn) {
    pushItem({
      kind: "notice",
      id: uid(),
      tone: "error",
      text: "Conecte uma IA em Configurações › Conexões de IA para usar o agente.",
    });
    return null;
  }
  if (!conn.model) {
    pushItem({
      kind: "notice",
      id: uid(),
      tone: "error",
      text: `A conexão "${conn.name}" está sem modelo. Escolha um em Configurações › Conexões de IA.`,
    });
    return null;
  }
  const provider = conn.provider;
  const model: string = conn.model;
  // Limite de gasto do mês atingido nesta conta: nem começa.
  if (spendStatus(spendOf(provider).spent, spendOf(provider).limit) === "reached") {
    pushItem({ kind: "notice", id: uid(), tone: "error", text: limitText(provider), budget: true });
    return null;
  }
  const selection = withSelection ? activeEditor.current?.selection().text?.trim() || undefined : undefined;
  const userText = selection
    ? `${text}\n\nTrecho selecionado no editor:\n\`\`\`\n${selection.slice(0, 20_000)}\n\`\`\``
    : text;
  pushItem({ kind: "user", id: uid(), text, ...(selection ? { selection } : {}) });
  const reasoning = resolveEffort(state.effort, text);
  beginCheckpoint(ws.detail.project.id, text);
  const ctl = new AbortController();
  controller = ctl;
  useAgentStore.setState({ running: true });
  const system = systemPrompt(state.mode);
  const context = projectContext(ws);
  const tools = toolDefs({ desktop: true });
  const start: AssistantMessage[] = [
    ...useAgentStore.getState().messages,
    { role: "user", content: [{ type: "text", text: userText }] },
  ];
  useAgentStore.setState({ messages: start });

  // Texto e raciocínio que chegam aos poucos: um item por bloco, do turno atual.
  let textId: string | null = null;
  let thinkingId: string | null = null;
  let turnText = false;
  /** Itens de texto que o stream criou neste turno (a resposta final corrige o que faltou). */
  let turnTextIds: string[] = [];
  /** O turno já terminou: eventos atrasados dele não criam nada. */
  let closed = false;
  const toolIds = new Set<string>();
  let budgetHit = false;
  let modelFix: { model: string; alternative?: string } | null = null;
  /**
   * Cada resposta do modelo é cobrada na hora (estimativa em US$): o gasto do mês sobe mesmo se a
   * tarefa for interrompida, e o aviso de 80% aparece uma vez por mês e conta.
   */
  const charge = (usedModel: string, u: AssistantUsage) => {
    const cost = assistantCost(
      provider,
      usedModel,
      u,
      knownPrice(provider, usedModel) ?? knownPrice(provider, model!),
    );
    useAgentStore.setState((s) => ({
      usage: {
        inputTokens: s.usage.inputTokens + u.inputTokens,
        outputTokens: s.usage.outputTokens + u.outputTokens,
        cacheReadTokens: s.usage.cacheReadTokens + u.cacheReadTokens,
        cacheWriteTokens: s.usage.cacheWriteTokens + u.cacheWriteTokens,
        cost: (s.usage.cost ?? 0) + cost,
      },
    }));
    if (provider === "anthropic")
      useAiUsage.getState().record(
        "claude",
        ws.detail.project.id.toLowerCase(),
        {
          requests: 1,
          input: u.inputTokens,
          output: u.outputTokens,
          cacheRead: u.cacheReadTokens,
          cacheWrite: u.cacheWriteTokens,
          cost,
        },
        { name: ws.detail.project.name, model: usedModel, feature: "Agente" },
      );
    else useAgentSpend.getState().add(provider, cost);
    const { spent, limit } = spendOf(provider);
    if (spendStatus(spent, limit) === "near" && useAgentSpend.getState().warnOnce(provider))
      pushItem({
        kind: "notice",
        id: uid(),
        tone: "info",
        text: `Você já usou ${formatSpend(spent)} dos ${formatSpend(limit!)} do mês em ${providerLabel(provider)}. Ao chegar no limite, o agente para.`,
        budget: true,
      });
  };
  const off = api.onEvent((e) => {
    if (e.requestId !== currentRequest || closed) return;
    if (e.type === "text") {
      thinkingId = null;
      if (!textId) {
        textId = uid();
        turnText = true;
        turnTextIds.push(textId);
        pushItem({ kind: "text", id: textId, text: "" });
      }
      const id = textId;
      patchItem(id, (i) => (i.kind === "text" ? { ...i, text: i.text + e.text } : i));
    } else if (e.type === "thinking") {
      if (!thinkingId) {
        thinkingId = uid();
        pushItem({ kind: "thinking", id: thinkingId, text: "" });
      }
      const id = thinkingId;
      patchItem(id, (i) => (i.kind === "thinking" ? { ...i, text: i.text + e.text } : i));
    } else if (e.type === "tool") {
      // A resposta final pode chegar antes dos últimos eventos: o passo já existe.
      if (toolIds.has(e.id)) return;
      textId = null;
      thinkingId = null;
      const spec = toolByName(e.name);
      toolIds.add(e.id);
      pushItem({
        kind: "tool",
        id: e.id,
        name: e.name,
        tool: spec?.kind ?? "read",
        status: "preparing",
        title: "Preparando…",
      });
    }
  });

  try {
    const result = await runLoop(start, {
      signal: ctl.signal,
      maxTurns: 80,
      turn: async (messages) => {
        textId = null;
        thinkingId = null;
        turnText = false;
        turnTextIds = [];
        closed = false;
        const requestId = uid();
        currentRequest = requestId;
        // O limite pode ter sido atingido no meio da tarefa: para antes do próximo pedido.
        if (spendStatus(spendOf(provider).spent, spendOf(provider).limit) === "reached") {
          budgetHit = true;
          return { ok: false as const, error: limitText(provider) };
        }
        const res = await api.turn({
          requestId,
          connectionId: conn.id,
          provider,
          model,
          effort: reasoning,
          system,
          context,
          messages,
          tools,
        });
        if (!res.ok && res.unavailable) modelFix = { model };
        if (res.ok && res.usage) charge(res.model || model!, res.usage);
        return res;
      },
      onAssistant: (message) => {
        closed = true;
        // Texto que não chegou pelos eventos (resposta mais rápida que o stream): vale o final.
        const finals = message.content.flatMap((b) => (b.type === "text" ? [b.text] : []));
        const finalText = finals.join("\n\n");
        if (finalText && !turnText) pushItem({ kind: "text", id: uid(), text: finalText });
        else if (finals.length && turnTextIds.length) {
          // A resposta completa chega por outro canal que os pedaços do stream, e pode chegar antes
          // dos últimos: o texto final sempre vale (antes, o fim da frase se perdia).
          if (finals.length === turnTextIds.length)
            turnTextIds.forEach((id, i) =>
              patchItem(id, (it) => (it.kind === "text" ? { ...it, text: finals[i]! } : it)),
            );
          else {
            const [first, ...rest] = turnTextIds;
            patchItem(first!, (it) => (it.kind === "text" ? { ...it, text: finalText } : it));
            const drop = new Set(rest);
            useAgentStore.setState((s) => ({ items: s.items.filter((it) => !drop.has(it.id)) }));
          }
        }
        // Chamadas anunciadas pelo stream que não vieram na resposta final saem da lista.
        const final = new Set(message.content.flatMap((b) => (b.type === "tool_use" ? [b.id] : [])));
        useAgentStore.setState((s) => ({
          items: s.items.filter(
            (i) => !(i.kind === "tool" && i.status === "preparing" && toolIds.has(i.id) && !final.has(i.id)),
          ),
        }));
        for (const b of message.content) {
          if (b.type !== "tool_use" || toolIds.has(b.id)) continue;
          toolIds.add(b.id);
          pushItem({
            kind: "tool",
            id: b.id,
            name: b.name,
            tool: toolByName(b.name)?.kind ?? "read",
            status: "preparing",
            title: "Preparando…",
          });
        }
      },
      runTool: (use) => runTool(ws, use, ctl.signal),
      // Leituras (ler, listar, procurar) rodam juntas: o agente age mais rápido.
      parallel: (use) => toolByName(use.name)?.kind === "read",
    });
    useAgentStore.setState({ messages: result.messages });
    const notice: Record<LoopStop, string | null> = {
      end: null,
      cancelled: "Interrompido. Escreva o que fazer a seguir.",
      max_tokens: "A resposta passou do tamanho máximo. Peça para continuar.",
      refusal: "O modelo recusou este pedido.",
      limit: "O agente chegou ao limite de passos desta tarefa. Peça para continuar se faltar algo.",
      error: result.error?.message ?? "Algo deu errado.",
    };
    const text = notice[result.stop];
    if (text)
      pushItem({
        kind: "notice",
        id: uid(),
        tone: result.stop === "cancelled" ? "info" : "error",
        text,
        ...(result.error?.auth ? { auth: true } : {}),
        ...(budgetHit ? { budget: true } : {}),
        ...(modelFix ? { modelFix } : {}),
      });
    // Fim da resposta: o que mudou (com "Desfazer") e a avaliação.
    if (result.stop !== "error" && result.stop !== "cancelled") {
      const cp = currentCheckpoint();
      pushItem({
        kind: "done",
        id: uid(),
        provider,
        model: result.model ?? model!,
        effort: reasoning,
        ...(cp ? { checkpointId: cp.id, files: Object.keys(cp.files) } : {}),
      });
    } else {
      const cp = currentCheckpoint();
      if (cp)
        pushItem({
          kind: "done",
          id: uid(),
          provider,
          model: model!,
          effort: reasoning,
          checkpointId: cp.id,
          files: Object.keys(cp.files),
        });
    }
    return result.stop;
  } finally {
    off();
    saveConversation();
    // Passos que ficaram no meio (interrompido) não ficam girando.
    useAgentStore.setState((s) => ({
      running: false,
      items: s.items.map((i) =>
        i.kind === "tool" && (i.status === "preparing" || i.status === "waiting" || i.status === "running")
          ? { ...i, status: "denied" as const, note: "interrompido" }
          : i,
      ),
    }));
    controller = null;
    currentRequest = null;
  }
}

async function runTool(
  ws: WorkspaceContextValue,
  use: { id: string; name: string; input: Record<string, unknown> },
  signal: AbortSignal,
) {
  const spec = toolByName(use.name);
  if (!toolIds(use.id))
    pushItem({
      kind: "tool",
      id: use.id,
      name: use.name,
      tool: spec?.kind ?? "read",
      status: "preparing",
      title: use.name,
    });
  if (!spec) {
    patchTool(use.id, { status: "error", title: use.name, result: "Ferramenta desconhecida." });
    return { content: `A ferramenta ${use.name} não existe.`, isError: true };
  }
  const requestId = uid();
  let prepared;
  try {
    checkInput(spec, use.input);
    prepared = await spec.prepare(use.input, {
      ws,
      signal,
      requestId,
      onOutput: (text) =>
        patchItem(use.id, (i) =>
          i.kind === "tool" ? { ...i, output: ((i.output ?? "") + text).slice(-20_000) } : i,
        ),
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    patchTool(use.id, { status: "error", title: friendlyTitle(use.name, use.input), result: message });
    return { content: message, isError: true };
  }
  patchTool(use.id, {
    title: prepared.title,
    ...(prepared.patch ? { patch: prepared.patch } : {}),
    ...(prepared.path ? { path: prepared.path } : {}),
    ...(prepared.command ? { command: prepared.command } : {}),
    ...(prepared.detail ? { detail: prepared.detail } : {}),
  });
  const { mode, granted } = useAgentStore.getState();
  const auto = autoDecision(spec.kind, mode, granted);
  if (auto === "deny") {
    patchTool(use.id, { status: "denied", note: "modo planejar" });
    return {
      content: "Bloqueado: o modo atual é só planejar. Descreva a mudança no plano em vez de executá-la.",
      isError: true,
    };
  }
  if (auto === "ask") {
    patchTool(use.id, { status: "waiting" });
    const decision = await new Promise<Decision>((resolve) => waiting.set(use.id, resolve));
    if (!decision.allow) {
      patchTool(use.id, { status: "denied", ...(decision.feedback ? { note: decision.feedback } : {}) });
      return {
        content: decision.feedback
          ? `A pessoa negou esta ação e disse: ${decision.feedback}`
          : signal.aborted
            ? "A pessoa interrompeu o agente."
            : "A pessoa negou esta ação. Não tente de novo do mesmo jeito; pergunte ou siga outro caminho.",
        isError: true,
      };
    }
  }
  if (signal.aborted) {
    patchTool(use.id, { status: "denied", note: "interrompido" });
    return { content: "A pessoa interrompeu o agente.", isError: true };
  }
  patchTool(use.id, { status: "running" });
  try {
    // Antes de mudar um arquivo: guarda o conteúdo de agora (para "Desfazer").
    const changes = (spec.kind === "edit" || spec.kind === "delete") && prepared.path ? prepared.path : null;
    const projectId = ws.detail.project.id;
    if (changes) {
      await rememberOriginal(ws.session, changes).catch(() => undefined);
      // Revisão de "arquivos alterados" (diff, Aprovar, Desfazer).
      recordBefore(projectId, "agent", changes, await readFile(ws.session, changes).catch(() => null));
    }
    const out = await prepared.run();
    if (changes && !out.isError)
      recordAfter(projectId, "agent", changes, await readFile(ws.session, changes).catch(() => null));
    patchTool(use.id, {
      status: out.isError ? "error" : "done",
      result: out.content.slice(0, 20_000),
      ...(out.note ? { note: out.note } : {}),
    });
    return out;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    patchTool(use.id, { status: "error", result: message });
    return { content: message, isError: true };
  }
}

const toolIds = (id: string) => useAgentStore.getState().items.some((i) => i.id === id);

function friendlyTitle(name: string, input: Record<string, unknown>) {
  const path = typeof input.path === "string" ? input.path : "";
  const map: Record<string, string> = {
    list_files: "Listar arquivos",
    read_file: `Ler ${path}`,
    search: "Procurar no projeto",
    write_file: `Escrever ${path}`,
    edit_file: `Editar ${path}`,
    delete_file: `Apagar ${path}`,
    run_command: "Rodar comando",
    start_in_terminal: "Abrir no terminal",
    git_status: "Ver o Git",
    git_commit_push: "Commit e push",
    git_create_repository: "Criar repositório",
    git_link_repository: "Ligar repositório",
  };
  return map[name] ?? name;
}
